import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import api from '../services/api';
import CreateAdModal from '../components/p2p/CreateAdModal';
import PaymentMethodsTab from '../components/p2p/PaymentMethodsTab';
import { Search, Filter, Plus, ArrowRight, CreditCard, List, Edit, XCircle, CheckCircle, X, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

const P2PPage = () => {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateAdModal, setShowCreateAdModal] = useState(false);
    const [tokens, setTokens] = useState([]);
    const [activeTab, setActiveTab] = useState('marketplace'); // 'marketplace', 'my-ads', or 'payment-methods'
    const [myAds, setMyAds] = useState([]);
    const [myAdsLoading, setMyAdsLoading] = useState(false);

    // Trade Modal State
    const [showTradeModal, setShowTradeModal] = useState(false);
    const [selectedAd, setSelectedAd] = useState(null);
    const [tradeAmount, setTradeAmount] = useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [tradeLoading, setTradeLoading] = useState(false);

    // Filters
    const [type, setType] = useState('buy'); // 'buy' means I want to BUY, so I look for SELL ads
    const [asset, setAsset] = useState('');
    const [amount, setAmount] = useState('');
    const [fiatCurrency, setFiatCurrency] = useState('INR');

    useEffect(() => {
        fetchTokens();
    }, []);

    useEffect(() => {
        fetchAds();
    }, [type, asset, fiatCurrency]);

    const fetchTokens = async () => {
        try {
            const res = await api.get('/wallet/tokens');
            setTokens(res.data.tokens);
            // Don't set default asset - show all ads by default
        } catch (error) {
            console.error('Failed to fetch tokens:', error);
        }
    };

    const fetchAds = async () => {
        setLoading(true);
        try {
            // If I want to BUY, I need to find ads where type is 'sell'
            // If I want to SELL, I need to find ads where type is 'buy'
            const targetType = type === 'buy' ? 'sell' : 'buy';

            let url = `/p2p/ads?type=${targetType}`;
            if (asset) url += `&asset=${asset}`;
            if (fiatCurrency) url += `&fiatCurrency=${fiatCurrency}`;
            if (amount) url += `&amount=${amount}`;

            const res = await api.get(url);
            setAds(res.data.ads);
        } catch (error) {
            console.error('Failed to fetch ads:', error);
            toast.error('Failed to load P2P ads');
        } finally {
            setLoading(false);
        }
    };

    const fetchMyAds = async () => {
        setMyAdsLoading(true);
        try {
            const res = await api.get('/p2p/my-ads');
            setMyAds(res.data.ads);
        } catch (error) {
            console.error('Failed to fetch my ads:', error);
            toast.error('Failed to load your ads');
        } finally {
            setMyAdsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'my-ads') {
            fetchMyAds();
        }
    }, [activeTab]);

    const openTradeModal = (ad) => {
        if (!user) {
            toast.error('Please login to trade');
            return;
        }
        if (ad.userId === user.id) {
            toast.error('Cannot trade on your own ad');
            return;
        }
        setSelectedAd(ad);
        setTradeAmount('');
        setSelectedPaymentMethod(ad.paymentMethods[0] || '');
        setShowTradeModal(true);
    };

    const handleTrade = async () => {
        if (!tradeAmount || parseFloat(tradeAmount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        if (!selectedPaymentMethod) {
            toast.error('Please select a payment method');
            return;
        }

        setTradeLoading(true);
        try {
            // Calculate amounts based on input
            let cryptoAmount, fiatAmountVal;

            if (type === 'buy') {
                // User input is in Fiat for Buy
                fiatAmountVal = parseFloat(tradeAmount);
                cryptoAmount = fiatAmountVal / parseFloat(selectedAd.price);
            } else {
                // User input is in Crypto for Sell
                cryptoAmount = parseFloat(tradeAmount);
                fiatAmountVal = cryptoAmount * parseFloat(selectedAd.price);
            }

            // Validate limits
            if (fiatAmountVal < parseFloat(selectedAd.minLimit)) {
                toast.error(`Minimum limit is ₹${parseFloat(selectedAd.minLimit).toFixed(0)}`);
                setTradeLoading(false);
                return;
            }
            if (fiatAmountVal > parseFloat(selectedAd.maxLimit)) {
                toast.error(`Maximum limit is ₹${parseFloat(selectedAd.maxLimit).toFixed(0)}`);
                setTradeLoading(false);
                return;
            }

            const res = await api.post('/p2p/orders', {
                adId: selectedAd.id,
                amount: cryptoAmount,
                fiatAmount: fiatAmountVal,
                paymentMethod: selectedPaymentMethod
            });

            toast.success('Order created successfully');
            setShowTradeModal(false);
            navigate(`/p2p/orders/${res.data.order.id}`);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to create order');
        } finally {
            setTradeLoading(false);
        }
    };

    // Calculate crypto amount from fiat input or vice versa
    const getCalculatedAmount = () => {
        if (!tradeAmount || !selectedAd) return { crypto: 0, fiat: 0 };

        if (type === 'buy') {
            const fiat = parseFloat(tradeAmount);
            const crypto = fiat / parseFloat(selectedAd.price);
            return { crypto: crypto.toFixed(6), fiat: fiat.toFixed(2) };
        } else {
            const crypto = parseFloat(tradeAmount);
            const fiat = crypto * parseFloat(selectedAd.price);
            return { crypto: crypto.toFixed(6), fiat: fiat.toFixed(2) };
        }
    };

    return (
        <div className="min-h-screen bg-[#0b0e11] text-gray-100 font-sans">
            <Navbar />

            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header & Actions */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">P2P Trading</h1>
                        <p className="text-gray-400">Buy and sell cryptocurrencies directly with other users</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate('/p2p/orders')}
                            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-bold transition-all"
                        >
                            My Orders
                        </button>
                        <button
                            onClick={() => setShowCreateAdModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-blue-900/20"
                        >
                            <Plus size={20} />
                            Post Ad
                        </button>
                    </div>
                </div>

                {/* Main Tab Bar */}
                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setActiveTab('marketplace')}
                        className={`px-6 py-3 rounded-xl font-bold text-sm transition-all ${activeTab === 'marketplace'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-white'
                            }`}
                    >
                        Marketplace
                    </button>
                    <button
                        onClick={() => setActiveTab('my-ads')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${activeTab === 'my-ads'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-white'
                            }`}
                    >
                        <List size={18} />
                        My Ads
                    </button>
                    <button
                        onClick={() => setActiveTab('payment-methods')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${activeTab === 'payment-methods'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-white'
                            }`}
                    >
                        <CreditCard size={18} />
                        Payment Methods
                    </button>
                </div>

                {activeTab === 'payment-methods' ? (
                    <PaymentMethodsTab />
                ) : activeTab === 'my-ads' ? (
                    /* My Ads Section */
                    <div className="space-y-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-white">Your Advertisements</h2>
                            <button
                                onClick={() => setShowCreateAdModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold text-sm"
                            >
                                <Plus size={18} />
                                Create New Ad
                            </button>
                        </div>

                        {myAdsLoading ? (
                            <div className="text-center py-20 text-gray-500">Loading your ads...</div>
                        ) : myAds.length > 0 ? (
                            myAds.map(ad => (
                                <div key={ad.id} className="bg-[#181a20] rounded-2xl border border-gray-800 p-6">
                                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <span className={`px-3 py-1 rounded-lg text-sm font-bold ${ad.type === 'sell'
                                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                                : 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                }`}>
                                                {ad.type.toUpperCase()}
                                            </span>
                                            <div>
                                                <div className="text-white font-bold">{ad.asset}</div>
                                                <div className="text-sm text-gray-400">
                                                    Price: ₹{parseFloat(ad.price).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <div className="text-sm text-gray-400">Amount</div>
                                                <div className="text-white font-medium">
                                                    {parseFloat(ad.remainingAmount).toFixed(4)} / {parseFloat(ad.totalAmount).toFixed(4)}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm text-gray-400">Limits</div>
                                                <div className="text-white font-medium">
                                                    ₹{parseFloat(ad.minLimit).toFixed(0)} - ₹{parseFloat(ad.maxLimit).toFixed(0)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className={`flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium ${ad.status === 'active'
                                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                : ad.status === 'completed'
                                                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                                    : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                                                }`}>
                                                {ad.status === 'active' && <CheckCircle size={14} />}
                                                {ad.status === 'cancelled' && <XCircle size={14} />}
                                                {ad.status.charAt(0).toUpperCase() + ad.status.slice(1)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-20 text-gray-500 bg-[#181a20] rounded-2xl border border-gray-800">
                                <List size={48} className="mx-auto mb-4 text-gray-600" />
                                <p className="text-lg font-medium mb-2">No ads yet</p>
                                <p className="text-sm">Create your first advertisement to start trading!</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <>

                        {/* Filters */}
                        <div className="bg-[#181a20] p-4 rounded-2xl border border-gray-800 mb-6">
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex bg-[#0b0e11] p-1 rounded-xl">
                                    <button
                                        onClick={() => setType('buy')}
                                        className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${type === 'buy'
                                            ? 'bg-green-500 text-white shadow-lg'
                                            : 'text-gray-400 hover:text-white'
                                            }`}
                                    >
                                        Buy
                                    </button>
                                    <button
                                        onClick={() => setType('sell')}
                                        className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${type === 'sell'
                                            ? 'bg-red-500 text-white shadow-lg'
                                            : 'text-gray-400 hover:text-white'
                                            }`}
                                    >
                                        Sell
                                    </button>
                                </div>

                                <div className="h-8 w-px bg-gray-800 hidden md:block"></div>

                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-400 font-medium">Asset:</span>
                                    <select
                                        value={asset}
                                        onChange={(e) => setAsset(e.target.value)}
                                        className="bg-[#0b0e11] border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500"
                                    >
                                        <option value="">All Coins</option>
                                        {tokens.map(token => (
                                            <option key={token.id} value={token.symbol}>{token.symbol}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-400 font-medium">Amount:</span>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            placeholder="Enter amount"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="bg-[#0b0e11] border border-gray-700 rounded-lg pl-3 pr-8 py-1.5 text-white text-sm focus:outline-none focus:border-blue-500 w-32"
                                        />
                                        <span className="absolute right-3 top-1.5 text-xs text-gray-500">{fiatCurrency}</span>
                                    </div>
                                </div>

                                <button
                                    onClick={fetchAds}
                                    className="ml-auto p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all"
                                >
                                    <Search size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Ads List */}
                        <div className="space-y-4">
                            {loading ? (
                                <div className="text-center py-20 text-gray-500">Loading ads...</div>
                            ) : ads.length > 0 ? (
                                ads.map(ad => (
                                    <div key={ad.id} className="bg-[#181a20] rounded-2xl border border-gray-800 p-6 hover:border-gray-700 transition-all group">
                                        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                                                        {ad.user?.firstName?.charAt(0) || ad.user?.email?.charAt(0) || 'U'}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-white">
                                                            {ad.user?.firstName && ad.user?.lastName
                                                                ? `${ad.user.firstName} ${ad.user.lastName}`
                                                                : ad.user?.email?.split('@')[0] || 'Anonymous'}
                                                        </div>
                                                        <div className="text-xs text-gray-500">Verified Trader</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex-1 text-center md:text-left">
                                                <div className="text-xs text-gray-400 mb-1">Price</div>
                                                <div className="text-2xl font-bold text-white">
                                                    {parseFloat(ad.price).toFixed(2)} <span className="text-sm text-gray-500 font-medium">{ad.fiatCurrency}</span>
                                                </div>
                                            </div>

                                            <div className="flex-1 text-center md:text-left">
                                                <div className="text-xs text-gray-400 mb-1">Available / Limit</div>
                                                <div className="text-sm font-medium text-white">
                                                    <span className="text-gray-300">Available:</span> {parseFloat(ad.remainingAmount).toFixed(4)} {ad.asset}
                                                </div>
                                                <div className="text-sm font-medium text-white">
                                                    <span className="text-gray-300">Limit:</span> {parseFloat(ad.minLimit).toFixed(0)} - {parseFloat(ad.maxLimit).toFixed(0)} {ad.fiatCurrency}
                                                </div>
                                            </div>

                                            <div className="flex-1 text-center md:text-left">
                                                <div className="text-xs text-gray-400 mb-1">Payment</div>
                                                <div className="flex flex-wrap gap-1 justify-center md:justify-start">
                                                    {ad.paymentMethods.map(method => (
                                                        <span key={method} className="px-2 py-0.5 bg-gray-800 rounded text-xs text-blue-400 border border-gray-700">
                                                            {method}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <button
                                                    onClick={() => openTradeModal(ad)}
                                                    className={`px-8 py-3 rounded-xl font-bold text-white transition-all shadow-lg ${type === 'buy'
                                                        ? 'bg-green-600 hover:bg-green-500 shadow-green-900/20'
                                                        : 'bg-red-600 hover:bg-red-500 shadow-red-900/20'
                                                        }`}
                                                >
                                                    {type === 'buy' ? 'Buy' : 'Sell'} {ad.asset}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-20 text-gray-500">
                                    <p className="text-lg font-medium mb-2">No ads found</p>
                                    <p className="text-sm">Try adjusting your filters or be the first to post an ad!</p>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {showCreateAdModal && (
                    <CreateAdModal
                        onClose={() => setShowCreateAdModal(false)}
                        onSuccess={fetchAds}
                        tokens={tokens}
                    />
                )}

                {/* Trade Modal */}
                {showTradeModal && selectedAd && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-[#181a20] rounded-2xl border border-gray-700 w-full max-w-md shadow-2xl">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-700">
                                <div>
                                    <h3 className="text-xl font-bold text-white">
                                        {type === 'buy' ? 'Buy' : 'Sell'} {selectedAd.asset}
                                    </h3>
                                    <p className="text-sm text-gray-400">
                                        From {selectedAd.user?.firstName || selectedAd.user?.email?.split('@')[0] || 'Trader'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowTradeModal(false)}
                                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    <X size={20} className="text-gray-400" />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-5">
                                {/* Price Info */}
                                <div className="flex items-center justify-between p-4 bg-[#0b0e11] rounded-xl border border-gray-700">
                                    <span className="text-gray-400">Price</span>
                                    <span className="text-xl font-bold text-white">
                                        ₹{parseFloat(selectedAd.price).toFixed(2)} <span className="text-sm text-gray-500">per {selectedAd.asset}</span>
                                    </span>
                                </div>

                                {/* Amount Input */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        Enter Amount ({type === 'buy' ? fiatCurrency : selectedAd.asset})
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            placeholder={type === 'buy' ? 'Enter INR amount' : 'Enter crypto amount'}
                                            value={tradeAmount}
                                            onChange={(e) => setTradeAmount(e.target.value)}
                                            className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3 text-white text-lg focus:outline-none focus:border-blue-500 transition-colors"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                                            {type === 'buy' ? fiatCurrency : selectedAd.asset}
                                        </span>
                                    </div>
                                    <div className="flex justify-between mt-2 text-sm text-gray-500">
                                        <span>Limit: ₹{parseFloat(selectedAd.minLimit).toFixed(0)} - ₹{parseFloat(selectedAd.maxLimit).toFixed(0)}</span>
                                        <span>Available: {parseFloat(selectedAd.remainingAmount).toFixed(4)} {selectedAd.asset}</span>
                                    </div>
                                </div>

                                {/* Calculated Amount */}
                                {tradeAmount && parseFloat(tradeAmount) > 0 && (
                                    <div className="p-4 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-xl border border-blue-500/20">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-400">You will {type === 'buy' ? 'receive' : 'get'}</span>
                                            <span className="text-lg font-bold text-white">
                                                {type === 'buy'
                                                    ? `${getCalculatedAmount().crypto} ${selectedAd.asset}`
                                                    : `₹${getCalculatedAmount().fiat}`
                                                }
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Payment Method Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        Payment Method
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {selectedAd.paymentMethods.map(method => (
                                            <button
                                                key={method}
                                                onClick={() => setSelectedPaymentMethod(method)}
                                                className={`p-3 rounded-xl border text-sm font-medium transition-all ${selectedPaymentMethod === method
                                                    ? 'bg-blue-600 border-blue-500 text-white'
                                                    : 'bg-[#0b0e11] border-gray-700 text-gray-400 hover:border-gray-600'
                                                    }`}
                                            >
                                                {method}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-6 border-t border-gray-700 flex gap-3">
                                <button
                                    onClick={() => setShowTradeModal(false)}
                                    className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-bold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleTrade}
                                    disabled={tradeLoading || !tradeAmount}
                                    className={`flex-1 px-4 py-3 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed ${type === 'buy'
                                        ? 'bg-green-600 hover:bg-green-500'
                                        : 'bg-red-600 hover:bg-red-500'
                                        }`}
                                >
                                    {tradeLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full"></span>
                                            Processing...
                                        </span>
                                    ) : (
                                        `${type === 'buy' ? 'Buy' : 'Sell'} ${selectedAd.asset}`
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default P2PPage;
